import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AdminApiError } from '../../api/admin-client.js';
import {
  activateAdminBrand,
  createAdminBrand,
  deactivateAdminBrand,
  fetchAdminBrands,
  updateAdminBrand,
  type CreateBrandInput,
  type UpdateBrandInput,
} from '../api/catalog-api.js';

const BRANDS_KEY = ['admin', 'catalog', 'brands'] as const;

async function requireToken(getToken: () => Promise<string | null>): Promise<string> {
  const token = await getToken();
  if (!token) {
    throw new AdminApiError('AUTHENTICATION_ERROR', 'Your session has expired. Please sign in again.', 401);
  }
  return token;
}

export function useAdminBrands(params: { isActive?: boolean } = {}) {
  const { isLoaded, isSignedIn, getToken } = useAuth();
  return useQuery({
    queryKey: [...BRANDS_KEY, params],
    queryFn: async ({ signal }) =>
      fetchAdminBrands({ ...params, limit: 100, offset: 0 }, { token: await requireToken(getToken), signal }),
    enabled: isLoaded && isSignedIn === true,
    staleTime: 30_000,
  });
}

export function useAdminBrandMutations() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const invalidate = () => void queryClient.invalidateQueries({ queryKey: BRANDS_KEY });

  const create = useMutation({
    mutationFn: async (input: CreateBrandInput) =>
      createAdminBrand(input, { token: await requireToken(getToken) }),
    onSuccess: invalidate,
  });

  const update = useMutation({
    mutationFn: async ({ id, input }: { id: string; input: UpdateBrandInput }) =>
      updateAdminBrand(id, input, { token: await requireToken(getToken) }),
    onSuccess: invalidate,
  });

  const activate = useMutation({
    mutationFn: async (id: string) => activateAdminBrand(id, { token: await requireToken(getToken) }),
    onSuccess: invalidate,
  });

  const deactivate = useMutation({
    mutationFn: async (id: string) => deactivateAdminBrand(id, { token: await requireToken(getToken) }),
    onSuccess: invalidate,
  });

  return { create, update, activate, deactivate };
}
